import { useParams } from 'react-router-dom';
import {
  ArrowCircleDownIcon,
  ArrowCircleUpIcon,
  CalendarDotsIcon,
  ClockIcon,
  DownloadSimpleIcon,
  HashIcon,
  PencilIcon,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import useSWR from 'swr';

import { OrderDocumentType, OrderLineStopType, OrderStatus } from 'src/types/enums';
import { fetchCustomerPortalOrder, sendCustomerPortalCmrReminder, updateCustomerPortalOrder } from './models';
import { linkOrderDocument, unlinkOrderDocument } from '../../order/models';
import type { OrderDocumentTypeValue } from '../../order/endpoints/shared.types';
import { nullthrows } from '../../../utils/invariant';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { PageHeader } from '../../../components/PageHeader';
import { OrderStatusTag } from '../../order/components/OrderStatusTag';
import { DataField } from '../../../components/DataField';
import { formatDate, formatDateTime, formatInputTime, sortDate } from '../../../utils/date';
import { useTranslation } from '../../../contexts/translation-context';
import { decimateAndFormatNumber } from '../../../utils/number';
import { calculateLinesTotalExclVat } from '../../order/utils/price';
import { OrderComplaintDialog } from './CustomerPortalOrderComplaintDialog';
import { TrailerTypeIcon } from '../../order/pages/order/TrailerTypes';
import { LoadingMethodIcon } from '../../order/pages/order/LoadingMethods';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import { getDisplayError } from '@utils/get-display-error';
import { LinkButton } from 'components/button/ButtonLink';
import { Card } from 'components/Card';
import { PageHeading } from 'components/PageHeading';
import { RatingDatafield } from 'src/app/order/components/RatingDatafield';
import { captureException } from '@sentry/react';
import { Button } from 'components/button/Button';
import { useDownloadDocument } from 'src/app/document/hooks/useDownloadDocument';
import { MultiFileUploaderButton } from 'src/app/document/components/MultiFileUploaderButton';
import { InputDialog } from 'components/dialog/InputDialog';
import { OrderRouteMap } from 'components/map/OrderRouteMap';
import { getOrderRouteData } from '../../order/models';
import { useOrderMapLocation } from 'src/app/order/hooks/useOrderMapLocation';
import { DelayedSpinner } from 'components/Spinner';

export const CustomerPortalOrderPage = () => {
  const { i18n } = useTranslation();
  const { orderId: _orderId } = useParams<{ orderId: string }>();
  const orderId = nullthrows(_orderId, 'Order ID is required');
  const { data, isLoading, mutate } = useSWR(['customer-portal-order', orderId], () =>
    fetchCustomerPortalOrder(orderId),
  );
  const { downloadFile } = useDownloadDocument();

  // Revalidate the SWR cache after REST mutations.
  const refreshOrder = async () => {
    await mutate();
  };

  // Fetch route data and tracking events for map
  const { data: routeData } = useSWR(`order-route-data-${orderId}`, () => getOrderRouteData(orderId));
  const {
    driverLocation,
    driverHeading,
    telemetryLastSeenAt,
    trailerLocation,
    trailerHeading,
    trailerPlate,
    trailerLastSeenAt,
  } = useOrderMapLocation(orderId);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  const cmrDocument = data?.documents.find((v) => v.type === OrderDocumentType.Cmr)?.document;
  const sustainabilityReportDocument = data?.documents.find(
    (v) => v.type === OrderDocumentType.SustainabilityReport,
  )?.document;
  const order = nullthrows(data, 'Order not found');
  const executionDate = order.lines
    .map((l) => {
      return l.stops.filter((s) => s.type === OrderLineStopType.Load).sort((a, b) => sortDate(a.date, b.date))[0]?.date;
    })
    .sort(sortDate)[0];
  const distance = Math.round(order.lines.reduce((acc, curr) => curr.distance + acc, 0) / 1000);
  const emissions = order.lines.reduce((acc, curr) => curr.emissions + acc, 0);

  const canSendCmrReminder =
    order.status === OrderStatus.Executed && !order.documents.find((v) => v.type === OrderDocumentType.Cmr);

  const totalWeight = order.lines.reduce(
    (acc, curr) => curr.contents.reduce((acc, curr) => acc + curr.weight, 0) + acc,
    0,
  );
  return (
    <>
      <PageHeader title={order.orderNumber ?? i18n('draft')} />

      <PageHeading
        leftSide={
          <div className="flex items-center gap-4">
            <Breadcrumb
              parentItem={{
                name: i18n('customerPortal.orders.orders'),
                to: '..',
              }}
              currentItem={order.orderNumber ?? i18n('draft')}
            />

            <div>
              <OrderStatusTag status={order.status} />
            </div>
          </div>
        }
        rightSide={
          <div className="flex gap-2">
            <LinkButton color="secondary" to={`../new/manual?copy-from=${orderId}`}>
              {i18n('customerPortal.order.duplicate')}
            </LinkButton>

            <OrderComplaintDialog
              triggerColor="secondary"
              orderId={order.id}
              isDisabled={order.status === OrderStatus.Draft || order.status === OrderStatus.Cancelled}
              onSuccess={refreshOrder}
            />

            {canSendCmrReminder && (
              <ConfirmDialog
                triggerText={i18n('customerPortal.cmrReminder.cmrReminder')}
                triggerColor="primary"
                title={i18n('customerPortal.cmrReminder.cmrReminder')}
                submitText={i18n('customerPortal.cmrReminder.sendReminder')}
                description={i18n('customerPortal.cmrReminder.confirmText')}
                onSubmit={async () => {
                  try {
                    await sendCustomerPortalCmrReminder(order.id);
                    toast.success(i18n('customerPortal.cmrReminder.confirmation'));
                  } catch (err: any) {
                    captureException(err);
                    toast.error(
                      i18n('customerPortal.cmrReminder.error', {
                        error: getDisplayError(err),
                      }),
                    );
                    throw err;
                  }
                }}
              />
            )}

            <Button
              color="primary"
              isDisabled={!cmrDocument}
              iconLeft={<DownloadSimpleIcon className="button-icon" />}
              onTrigger={() => (cmrDocument ? downloadFile(cmrDocument) : null)}
            >
              CMR
            </Button>

            <MultiFileUploaderButton
              buttonText={i18n('customerPortal.orders.attachments')}
              title={i18n('customerPortal.orders.attachments')}
              files={
                data?.documents.filter((v) => v.type === OrderDocumentType.CustomerAttachment).map((d) => d.document) ||
                []
              }
              onSubmit={async (documents) => {
                let count = 0;
                await Promise.allSettled(
                  documents.map(async (doc) => {
                    try {
                      await linkOrderDocument(order.id, {
                        documentId: doc.id,
                        type: OrderDocumentType.CustomerAttachment as OrderDocumentTypeValue,
                      });
                      count += 1;
                      toast.success(
                        i18n('customerPortal.orders.uploadedAttachment', {
                          document: doc.name,
                        }),
                      );
                    } catch (err) {
                      captureException(err);
                      toast.error(
                        i18n('customerPortal.orders.uploadedAttachment', {
                          document: doc.name,
                          error: getDisplayError(err),
                        }),
                      );
                    }
                  }),
                );
                refreshOrder();
              }}
              unlinkDocument={async (docId: string) => {
                try {
                  await unlinkOrderDocument(order.id, {
                    documentId: docId,
                    type: OrderDocumentType.CustomerAttachment as OrderDocumentTypeValue,
                  });
                  toast.success(i18n('customerPortal.orders.attachmentDeleted'));
                  refreshOrder();
                } catch (err) {
                  captureException(err);
                  toast.error(
                    i18n('customerPortal.orders.couldNotDeleteAttachment', {
                      error: getDisplayError(err),
                    }),
                  );
                }
              }}
              disableUpload={order.status === OrderStatus.Cancelled}
            />
          </div>
        }
      />

      <div className="grid xl:grid-cols-2 gap-4 px-4">
        <Card title={i18n('customerPortal.order.overview')}>
          <div className="grid grid-cols-2 gap-4">
            <DataField title={i18n('customerPortal.orders.invoiceRef')}>
              <div className="flex gap-2 items-center justify-between">
                <div>{order.customerRef || order.orderNumber || '-'}</div>
                {order.status === OrderStatus.Draft && (
                  <InputDialog
                    triggerText={<PencilIcon className="w-4 h-4" />}
                    triggerColor="secondary"
                    title={i18n('customerPortal.order.updateInvoiceRef')}
                    submitText={i18n('save')}
                    cancelText={i18n('cancel')}
                    labelText={i18n('customerPortal.orders.invoiceRef')}
                    onSubmit={async (value: string) => {
                      try {
                        await updateCustomerPortalOrder(orderId, {
                          customerRef: value,
                        });
                        await refreshOrder();
                        toast.success(i18n('customerPortal.order.updated'));
                      } catch (err) {
                        captureException(err);
                        toast.error(
                          i18n('customerPortal.order.couldNotUpdate', {
                            error: getDisplayError(err),
                          }),
                        );
                        throw err;
                      }
                    }}
                  />
                )}
              </div>
            </DataField>
            <DataField title={i18n('customerPortal.orders.creationDate')}>{formatDate(order.createdAt)}</DataField>
            {executionDate && (
              <DataField title={i18n('customerPortal.orders.executionDate')}>{formatDate(executionDate)}</DataField>
            )}
            {order.status === OrderStatus.Executed && <RatingDatafield order={order} onRated={refreshOrder} />}
            {order.responsibleUser && (
              <DataField title={i18n('customerPortal.orders.fileManager')}>{order.responsibleUser.name}</DataField>
            )}
            {order.cancellationReason && (
              <DataField title={i18n('customerPortal.orders.cancellationReason')}>{order.cancellationReason}</DataField>
            )}
          </div>

          <div className="flex flex-wrap gap-4 mt-4">
            {order.allowedTrailerTypes.map((trailerType) => {
              return (
                <div
                  className="bg-surface-sunken rounded flex flex-col items-center justify-center p-2 px-4"
                  key={`${order.id}-${trailerType}`}
                >
                  <TrailerTypeIcon type={trailerType} variant="regular" />
                </div>
              );
            })}
            {order.loadingMethod && (
              <div className="bg-surface-sunken rounded flex flex-col items-center justify-center p-2 px-4">
                <LoadingMethodIcon type={order.loadingMethod} variant="regular" />
              </div>
            )}
          </div>
        </Card>

        <div className="grid gap-4">
          {order.lines.map((l) => {
            const saleTotal = calculateLinesTotalExclVat(l.sales);

            return (
              <div key={l.id} className="grid gap-8">
                <Card title={i18n('customerPortal.orders.stops')}>
                  <div className="grid grid-cols-2 gap-4">
                    {l.stops.map((stop) => {
                      return (
                        <div key={stop.id}>
                          <div className="flex items-center gap-2 mb-2">
                            {stop.type === OrderLineStopType.Load ? (
                              <ArrowCircleUpIcon className="w-6 h-6 text-brand" />
                            ) : (
                              <ArrowCircleDownIcon className="w-6 h-6 text-brand" />
                            )}

                            <div className="text-fg-muted font-medium">
                              {stop.type === OrderLineStopType.Load
                                ? i18n('pages.orderSummary.load')
                                : i18n('pages.orderSummary.unload')}
                            </div>
                          </div>
                          <div className="mb-1">
                            <div>{stop.location.name}</div>
                            <div className="whitespace-nowrap">{`${stop.location.street} ${stop.location.streetNumber}`}</div>
                            <div>{`${stop.location.country}-${stop.location.postalCode}`}</div>
                          </div>
                          <div className="flex items-center">
                            <HashIcon className="w-4 h-4 mr-2" />
                            <div>{stop.reference || '-'}</div>
                          </div>
                          <div className="flex items-center">
                            <CalendarDotsIcon className="w-4 h-4 mr-2" />
                            <div>{formatDate(stop.date)}</div>
                          </div>
                          <div className="flex items-center">
                            <ClockIcon className="w-4 h-4 mr-2" />
                            <div className="whitespace-nowrap">{`${formatInputTime(stop.timeStart)} - ${formatInputTime(
                              stop.timeEnd,
                            )}`}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {!!l.sales.length && (
                  <Card title={i18n('customerPortal.orders.purchases')}>
                    {l.sales.map((sale) => {
                      return (
                        <div key={sale.id} className="grid grid-cols-4">
                          <div className="col-span-3 whitespace-nowrap">
                            {`${decimateAndFormatNumber(sale.amount, 2)} x ${sale.productType.nameNl}`}
                          </div>
                          <div className="whitespace-nowrap">
                            {`${decimateAndFormatNumber((sale.amount * sale.unitPrice) / 100, 2, {
                              decimalSeperator: ',',
                            })} EUR`}
                          </div>
                        </div>
                      );
                    })}

                    <div className="grid grid-cols-4 mb-2 border-t border-border pt-2 mt-2">
                      <div className="col-span-3 whitespace-nowrap font-medium">
                        {i18n('customerPortal.orders.total')}
                      </div>
                      <div className="whitespace-nowrap font-medium text-brand">
                        {`${decimateAndFormatNumber(saleTotal, 2, {
                          decimalSeperator: ',',
                        })} EUR`}
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            );
          })}
        </div>

        {/* Route Map */}
        {routeData && routeData.stops.length > 0 && (
          <div className="xl:col-span-2">
            <Card title={i18n('customerPortal.orders.route')}>
              <OrderRouteMap
                routeCoordinates={routeData.polyline ?? []}
                stops={routeData.stops
                  .filter((s) => s.coordinates !== null)
                  .map((s) => ({
                    type: s.type,
                    name: s.name,
                    coordinates: s.coordinates as [number, number],
                  }))}
                driverLocation={driverLocation}
                driverHeading={driverHeading}
                trailerLocation={trailerLocation}
                trailerHeading={trailerHeading}
                trailerLabel={trailerPlate}
              />
              {/* Which source the marker came from, when it is not the driver app: a telemetry position
                  is only as fresh as this page load, so it says when the truck last reported it. */}
              {telemetryLastSeenAt && (
                <p className="mt-2 text-sm text-fg-muted">
                  {i18n('liveTracking.lastSignal')}: {formatDateTime(telemetryLastSeenAt)}
                </p>
              )}
              {/* The trailer's own line, so the two markers are told apart: the plate names it, and its
                  timestamp says how old the position under its marker is. */}
              {trailerLastSeenAt && trailerPlate && (
                <p className="mt-2 text-sm text-fg-muted">
                  {i18n('liveTracking.trailer')}: {trailerPlate} · {i18n('liveTracking.lastSignal')}:{' '}
                  {formatDateTime(trailerLastSeenAt)}
                </p>
              )}
            </Card>
          </div>
        )}

        {emissions > 0 && (
          <Card
            title={i18n('customerPortal.orders.sustainability')}
            rightHeading={
              <div className="flex justify-between items-center mb-2">
                <Button
                  color="primary"
                  isDisabled={!sustainabilityReportDocument}
                  iconLeft={<DownloadSimpleIcon className="button-icon" />}
                  onTrigger={() => (sustainabilityReportDocument ? downloadFile(sustainabilityReportDocument) : null)}
                >
                  {i18n('customerPortal.orders.report')}
                </Button>
              </div>
            }
          >
            <div className="grid grid-cols-2 gap-4">
              <DataField title={i18n('customerPortal.orders.truck')}>Euro 6</DataField>
              <DataField title={i18n('customerPortal.orders.distance')}>{`${distance}km`}</DataField>
              <DataField title={i18n('customerPortal.orders.weight')}>{`${totalWeight / 100}kg`}</DataField>
              <DataField title={i18n('customerPortal.orders.emissions')}>{`${decimateAndFormatNumber(emissions, 3, {
                decimalSeperator: ',',
              })}kg co2eq`}</DataField>
            </div>
          </Card>
        )}

        {order.complaints.length > 0 && (
          <Card title={i18n('customerPortal.orders.complaints')}>
            <div className="grid gap-2">
              {order.complaints.map((v) => {
                return (
                  <div key={v.id}>
                    <div>{v.content}</div>

                    <div className="flex justify-between items-center mt-2">
                      <div className="text-sm text-fg-secondary">{formatDateTime(v.createdAt)}</div>
                      <div className="px-2 py-1 rounded-md inline-flex items-center bg-border-strong text-xs">
                        {i18n(`orderComplaintType.${v.type}`)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </>
  );
};
